'use client';

import Script from 'next/script';
import { Suspense, useEffect, useRef } from 'react';

import {
    useEffectOnce,
    useLoyaltyContext,
    usePageLayout,
} from '@libs/common-next';
import { trackUserAuthentication } from '../../gtm';
import { useGtmContainerWithLocale } from '../../hooks';
import { PageViewTracking } from '../PageViewTracking';

export function NoScriptIframe() {
    const { gtmContainerId, gtmAuth, gtmPreview } = useGtmContainerWithLocale();

    return (
        <noscript>
            <iframe
                title="gtm-noscript"
                src={`https://www.googletagmanager.com/ns.html?id=${gtmContainerId}&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x`}
                height="0"
                width="0"
                style={{ display: 'none', visibility: 'hidden' }}
            />
        </noscript>
    );
}

export function GoogleTagManagerScript() {
    const { gtmContainerId, gtmAuth, gtmPreview } = useGtmContainerWithLocale();
    const { ocapi, loyalty, isAuthenticated, user, userData } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();
    const shouldTriggerUserAuthenticationEventRef = useRef(true);

    // TODO: remove effect after go-live
    // Track user authentication for legacy login (page load)
    useEffect(() => {
        const { customer } = ocapi.data?.session ?? {};
        if (
            customer &&
            customer.auth_type !== 'guest' &&
            customer.c_loyaltyId &&
            loyalty &&
            shouldTriggerUserAuthenticationEventRef.current
        ) {
            trackUserAuthentication({
                loyaltyId: customer.c_loyaltyId,
                storeId: customer.c_storeID ?? null,
                totalTreatsPoints: loyalty.totalPoints ?? 0,
                loginState: true,
            });
            shouldTriggerUserAuthenticationEventRef.current = false;
        }
    }, [ocapi.data?.session, loyalty]);

    // Track user authentication for velocity login (page load)
    useEffect(() => {
        if (
            isAuthenticated === 'velocity' &&
            memberPoints &&
            shouldTriggerUserAuthenticationEventRef.current
        ) {
            trackUserAuthentication({
                loyaltyId: user?.loyaltyid?.toString() ?? '',
                storeId: userData?.profile?.storeNumber ?? null,
                totalTreatsPoints: memberPoints?.availablePoints ?? 0,
                loginState: true,
            });
            shouldTriggerUserAuthenticationEventRef.current = false;
        }
    }, [isAuthenticated, memberPoints, user, userData]);

    useEffect(() => {
        if (!isAuthenticated) {
            shouldTriggerUserAuthenticationEventRef.current = true;
        }
    }, [isAuthenticated]);

    useEffectOnce(() => {
        if (typeof window !== 'undefined') {
            window.flags = {
                ...window.flags,
                IS_PERFORMANCE_MODE:
                    process.env.NEXT_PUBLIC_PERFORMANCE_MODE === 'true',
            };
        }
    });

    return (
        <>
            <NoScriptIframe />
            {/* eslint-disable-next-line @next/next/no-before-interactive-script-outside-document -- This is a script should be loaded in the head */}
            <Script
                id="gtm-script"
                strategy="beforeInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                        'https://www.googletagmanager.com/gtm.js?id='+i+dl+ '&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x';
                        f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','${gtmContainerId}');
                    `,
                }}
            />
            <Suspense fallback={null}>
                <PageViewTracking />
            </Suspense>
        </>
    );
}
